import { useState } from "react";
import {
  AppBar,
  styled,
  Toolbar,
  Box,
  useMediaQuery,
  useTheme,
  ClickAwayListener,
  Paper,
  Link as MuiLink,
  InputBase,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LOGO from "../assets/icons/logo.svg";
import CommonIconButton from "./Buttons/icon-button";

const CustomAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.background.default,
  boxShadow: theme.shadows[2],
}));

const MobileMenu = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  right: 16,
  left: 16,
  zIndex: 1200,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[4],
  padding: theme.spacing(1),
}));

const Header = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleMenu = () => {  setMenuOpen((prev) => !prev);}
  const closeMenu = () => setMenuOpen(false);

  const handleBlogsClick = () => {
    window.open('/blog/', '_blank', 'noopener,noreferrer');
    closeMenu();
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <CustomAppBar position="sticky">
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 3 },
          minHeight: { xs: 60, sm: 68 },
          position: "relative",
          gap: { xs: 1.5, sm: 3 },
        }}
      >
        {/* Left Side: Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "auto", md: 200 },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: 60, sm: 70 },
              height: { xs: 60, sm: 70 },
            }}
          >
            <Box
              component="img"
              src={LOGO}
              onClick={() => {
                navigate("/");
                closeMenu();
              }}
              alt="CosmetixHub logo"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                cursor: "pointer",
              }}
            />
          </Box>
        </Box>

        {/* Center: Search Bar */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            maxWidth: { xs: "100%", sm: 500 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 38, sm: 42 },
              display: "flex",
              alignItems: "center",
              border: "1px solid #e8e8e8",
              borderRadius: "999px",
              backgroundColor: "#f9f9f9",
              overflow: "hidden",
              px: 2,
              transition: "all 0.3s ease",
              "&:focus-within": {
                borderColor: theme.palette.primary.main,
                backgroundColor: "#fff",
                boxShadow: `0 0 0 2px ${theme.palette.primary.light}25`,
              },
            }}
          >
            <InputBase
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                fontSize: { xs: "13px", sm: "14px" },
                color: "#444",
              }}
            />
            <CommonIconButton
              onClick={handleSearch}
              sx={{
                p: 0.5,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </CommonIconButton>
          </Box>
        </Box>

        {/* Right Side: Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: { xs: "auto", md: 200 },
            flexShrink: 0,
          }}
        >
          {isMobile ? (
            <CommonIconButton
              onClick={toggleMenu}
              sx={{ width: 40, height: 40 }}
            >
              {menuOpen ? (
                <CloseIcon sx={{ fontSize: 20, color: "#6a6d76" }} />
              ) : (
                <MenuIcon sx={{ fontSize: 20, color: "#6a6d76" }} />
              )}
            </CommonIconButton>
          ) : (
            <MuiLink
              onClick={handleBlogsClick}
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: "#6a6d76",
                cursor: "pointer",
                textDecoration: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                },
              }}
            >
              Blog
            </MuiLink>
          )}
        </Box>

        {/* Mobile Menu */}
        {isMobile && menuOpen ? (
          // <ClickAwayListener onClickAway={closeMenu}>
            <MobileMenu>
              <MuiLink
                onClick={handleBlogsClick}
                sx={{
                  display: "block",
                  py: 1.5,
                  px: 2,
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#6a6d76",
                  cursor: "pointer",
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Blog
              </MuiLink>
            </MobileMenu>
          // </ClickAwayListener>
        ) : null} 
      </Toolbar>
    </CustomAppBar>
  );
};

export default Header;
